import { NavLink, useNavigate } from "react-router-dom";
import {
  InboxIcon,
  ArrowRightOnRectangleIcon,
  GlobeAltIcon,
  ClipboardDocumentListIcon,
  ArrowsRightLeftIcon,
  KeyIcon,
  ShieldCheckIcon,
  FunnelIcon,
  QueueListIcon,
  BookOpenIcon,
  HomeIcon,
} from "@heroicons/react/24/outline";
import { useLogout, useMe } from "@/api/hooks";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const nav = [
  { to: "/", label: "Dashboard", icon: HomeIcon, end: true },
  { to: "/mailboxes", label: "Mailboxes", icon: InboxIcon },
  { to: "/aliases", label: "Aliases", icon: ArrowsRightLeftIcon },
  { to: "/domains", label: "Domains", icon: GlobeAltIcon },
  { to: "/dns", label: "DNS health", icon: ShieldCheckIcon },
  { to: "/dkim", label: "DKIM", icon: KeyIcon },
  { to: "/sieve", label: "Sieve", icon: FunnelIcon },
  { to: "/queue", label: "Queue", icon: QueueListIcon },
  { to: "/audit", label: "Audit", icon: ClipboardDocumentListIcon },
  { to: "/docs", label: "Docs", icon: BookOpenIcon },
];

export function AppShell({ children }: { children: React.ReactNode }) {
  const me = useMe();
  const logout = useLogout();
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen">
      <aside className="flex w-60 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground">
        <div className="border-b border-sidebar-border px-5 py-5">
          <div className="flex items-baseline gap-1 text-heading">
            <span className="text-base font-medium">Mail</span>
            <span className="text-base font-medium text-bitrefinery-red">Admin</span>
          </div>
          <div className="mt-1 truncate font-mono text-xs text-muted-foreground">
            {me.data?.email}
          </div>
        </div>
        <nav className="flex-1 space-y-1 p-3">
          {nav.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                  isActive
                    ? "bg-sidebar-accent font-medium text-sidebar-accent-foreground"
                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                )
              }
            >
              <Icon className="h-5 w-5" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-sidebar-border p-3">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-sidebar-foreground/70 hover:text-sidebar-accent-foreground"
            onClick={async () => {
              await logout.mutateAsync();
              navigate("/login", { replace: true });
            }}
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
            Sign out
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto">
        <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">{children}</div>
      </main>
    </div>
  );
}
